import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  ArrowLeft, Users, Mail, Phone, MapPin, Ticket, Clock, 
  AlertTriangle, CheckCircle, ChevronRight, Loader2, FileText, Bot 
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/customers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      } else {
        toast.error('Failed to load customer profile');
      }
    } catch (error) {
      console.error('Fetch customer error:', error);
      toast.error('Failed to load customer profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && token) {
      fetchCustomer();
    }
  }, [id, token]);

  const getStatusClass = (status) => {
    const classes = {
      'Customer': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'Lead': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      'Inactive': 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
    };
    return classes[status] || 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  };

  const getTicketStatusClass = (status) => {
    const classes = {
      'open': 'status-open',
      'in-progress': 'status-in-progress',
      'resolved': 'status-resolved',
      'closed': 'status-closed',
      'escalated': 'status-escalated'
    };
    return classes[status] || 'status-open';
  };

  const getTicketPriorityClass = (priority) => {
    const classes = {
      'high': 'priority-high',
      'medium': 'priority-medium',
      'low': 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-amber-500 text-white',
      'bg-emerald-500 text-white',
      'bg-violet-500 text-white',
      'bg-blue-500 text-white',
      'bg-rose-500 text-white',
      'bg-orange-500 text-white'
    ];
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    return colors[sum % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="support-card text-center py-12">
        <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
        <h2 className="text-lg font-bold mb-2">Customer Profile Not Found</h2>
        <Link to="/customers" className="text-primary hover:underline flex items-center gap-1 justify-center mt-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Customers List
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <Link to="/customers" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </Link>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-bold text-lg border shadow shrink-0 ${getAvatarColor(customer.company_name)}`}>
            {customer.company_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold">{customer.company_name}</h1>
              <span className={`border text-3xs px-2.5 py-0.5 rounded-full font-semibold uppercase ${getStatusClass(customer.status)}`}>
                {customer.status}
              </span>
              <span className="bg-primary/20 text-primary border border-primary/30 text-3xs px-2.5 py-0.5 rounded font-bold uppercase">
                {customer.role}
              </span>
            </div>
            <p className="text-muted-foreground mt-1">PIC Name: <span className="font-semibold text-foreground">{customer.name}</span></p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile Details Column (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Overview */}
          <div className="support-card">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-sm text-primary uppercase tracking-wider">
              <FileText className="w-4 h-4" />
              Overview Information
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">PIC</p>
                <p className="font-semibold">{customer.name || '-'}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Mobile Number</p>
                <p className="font-mono font-semibold">{customer.phone}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Email Address</p>
                <p className="font-semibold">{customer.email || '-'}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Location / Country</p>
                <p className="font-semibold">Healthcare - Malaysia</p>
              </div>
            </div>
          </div>

          {/* Initial Issue / Description */}
          <div className="support-card border-l-4 border-l-primary">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm text-primary uppercase tracking-wider">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Reported Issue / Notes
            </h3>
            <p className="text-sm leading-relaxed whitespace-pre-line text-foreground/90">
              {customer.issue || 'No issues currently reported by this customer.'}
            </p>
          </div>
        </div>

        {/* Support Tickets Column (1/3 width) */}
        <div className="space-y-6">
          {/* Related Tickets */}
          <div className="support-card">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-sm text-primary uppercase tracking-wider">
              <Ticket className="w-4 h-4" />
              Support Ticket History ({customer.tickets?.length || 0})
            </h3>
            
            <div className="space-y-3">
              {customer.tickets && customer.tickets.length > 0 ? (
                customer.tickets.map((tkt) => (
                  <div 
                    key={tkt.id}
                    className="p-3 bg-secondary/30 border border-border/40 rounded-lg hover:border-primary/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <Link 
                        to={`/tickets/${tkt.id}`} 
                        className="text-xs font-mono font-bold text-primary hover:underline"
                      >
                        {tkt.id}
                      </Link>
                      <span className={`status-badge text-3xs ${getTicketStatusClass(tkt.status)}`}>
                        {tkt.status}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-semibold truncate mb-2">{tkt.subject}</h4>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className={`status-badge text-3xs ${getTicketPriorityClass(tkt.priority)}`}>
                        {tkt.priority} priority
                      </span>
                      <span className="text-3xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(tkt.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground text-xs">
                  <Ticket className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  No support tickets found for this customer.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
